// Client for the xq-api console API. Authenticated calls send the Cognito ID
// token (API Gateway's JWT authoriser checks it; it carries custom:companyId).
import { useMemo } from "react";
import type { HostedDetails, SolarEstimateRequest, SolarLocateRequest, SolarPricing, WidgetBranding, WidgetPricing, WidgetProduct } from "@widget";
import { useAuth } from "./auth/AuthContext";
import { config } from "./config";

/** A widget as the console API returns it. branding null = defaults; pricing null = not priced. */
export interface Widget {
  widgetId: string;
  name: string;
  siteKey: string;
  status: "draft" | "ready" | "live"; // worked out by the API, see components/StatusChip
  statusMissing: { key: string; text: string; tab: string }[]; // what's left before it can run
  lastSeenOnSite?: string | null; // last load on one of its allowed websites
  lastSeenOrigin?: string | null;
  branding: WidgetBranding | null;
  pricing: WidgetPricing | null; // null = not priced yet
  // Heat pump estimator (and contact forms) or solar estimate: set once at creation.
  // The API always sends it (older widgets read as "ashp"); optional for safety.
  product?: WidgetProduct;
  // Solar estimates only: base, per-panel and inverter prices, panel and battery (null = the example defaults)
  solarPricing?: SolarPricing | null;
  domains?: string[]; // websites the widget may run on
  notifyEmails?: string[]; // who is emailed each new enquiry
  quotes?: { enabled: boolean } | null; // instant detailed quotes after the estimate (QuoteFlow plan)
  hosted?: HostedDetails | null; // hosted page header/footer (hosted widgets)
  hostedCode?: string | null; // page link: 4 generated characters, or the installer's own choice
  hostedCodeLocked?: boolean; // true once the installer has chosen the link (it can't change again)
  hostedUrl?: string | null; // https://go.<domain>/<link>
  createdAt: string;
  updatedAt: string;
}

/**
 * An address the company may send enquiries to. Enquiries only go to confirmed
 * ones: the signed-in user's own (method "account"), ones that clicked our email
 * link ("link"), or, in the SES sandbox, ones SES verified itself ("ses").
 */
export interface Recipient {
  email: string;
  status: "pending" | "confirmed";
  method?: "account" | "link" | "ses" | null;
  sentAt?: string | null;
  confirmedAt?: string | null;
}

/** What the console sends to POST /v1/widgets/{widgetId}/solar-preview (one of three actions). */
export type SolarPreviewRequest =
  | ({ action: "locate" } & SolarLocateRequest)
  | { action: "building"; lat: number; lng: number; terrace?: boolean }
  | ({ action: "estimate"; pricing?: SolarPricing } & SolarEstimateRequest);

/**
 * The plan line: LeadFlow ASHP ("estimate") or QuoteFlow, the upgrade that
 * includes both LeadFlows. LeadFlow Solar is ASHP's peer (its own line, or
 * included in QuoteFlow). Single page hosting is an option on each LeadFlow
 * (included in QuoteFlow); contact forms are an add-on that needs a product.
 */
export type BillingPlan = "estimate" | "quoteflow";
/** Everything with a price: LeadFlow ASHP, QuoteFlow, LeadFlow Solar and the add-ons. */
export type BillingPriceKey = BillingPlan | "solar" | "contactforms" | "hosting" | "solarhosting";
/** Stripe lines: everything with a price, plus old lines (per component, half-price solar). */
export type BillingLine = BillingPriceKey | "widget" | "hosted" | "solarbundle";
/**
 * What a component counts as: a contact form, a heat pump estimate component,
 * a solar estimate component or a hosted page (a hosted solar page is "hosted").
 */
export type ComponentCategory = "contact" | "estimate" | "hosted" | "solar" | "solarhosted";

/** The company's one Stripe subscription, as the webhook last saw it. */
export interface CompanySubscription {
  id: string;
  status: string; // Stripe status: active, trialing, past_due, canceled, incomplete, unpaid…
  interval: "monthly" | "yearly";
  quantities: Partial<Record<BillingLine, number>>;
  currentPeriodEnd: number | null; // epoch seconds
  cancelAtPeriodEnd: boolean;
}

/** Billing overview: the plan, what it puts live, each component, prices in pence ex VAT. */
export interface BillingOverview {
  subscription: CompanySubscription | null;
  active: boolean;
  plan: BillingPlan | ""; // LeadFlow ASHP or QuoteFlow ("" = neither)
  solar: boolean; // a LeadFlow Solar line; QuoteFlow includes solar without one
  hosting: number; // single page hosting bought for LeadFlow ASHP (0/1)
  solarHosting?: number; // single page hosting bought for LeadFlow Solar (0/1)
  contactForms: boolean; // the contact forms add-on (with any product)
  legacy: boolean; // bought per component before the plans
  // Live slots per category (solar: 0 or 3)
  limits: Record<ComponentCategory, number>;
  components: Record<ComponentCategory, { widgetId: string; name: string; style: string; covered: boolean }[]>;
  // Pence ex VAT
  prices: Partial<Record<BillingPriceKey, { monthly?: number; yearly?: number }>> | null;
  hasCustomer: boolean;
  demo?: boolean; // our demo company: everything runs without a subscription
}

/** Company details: Account → Profile, and the defaults for a hosted page's header/footer. */
export interface CompanyDetails {
  companyName: string;
  tagline: string;
  phone: string;
  email: string;
  website: string;
  address: string;
  companyNumber: string;
  vatNumber: string;
  privacyUrl: string;
}

/** Account → Profile: you, and your company. */
export interface CompanyProfile {
  user: { firstName: string; lastName: string; email: string; pictureUrl?: string }; // pictureUrl: SSO avatar
  company: CompanyDetails;
  ownerEmail: string;
}

/** A widget's acceptance of the current estimate-method terms. */
export interface TermsStatus {
  currentVersion: string;
  accepted: boolean;
  acceptance: { version: string; widgetId: string; acceptedAt: string; acceptedByEmail: string } | null;
}

/** Funnel stages, in order (matches STAGES in the stats API). */
// "detailed": leads that asked for a detailed quote (a subset of "lead", QuoteFlow)
// "scan": a solar estimate scanned the roof (solar widgets only, between address and quote)
export type FunnelStage = "loaded" | "opened" | "postcode" | "address" | "scan" | "quote" | "contact" | "lead" | "detailed";
export type StatsRange = "7d" | "30d" | "90d" | "180d" | "1y";
type StageCounts = Record<FunnelStage, number>;

/** Dashboard analytics: unique visitor sessions reaching each stage. */
export interface Stats {
  range: StatsRange;
  from: string;
  to: string;
  stages: FunnelStage[];
  totals: StageCounts;
  daily: ({ day: string } & StageCounts)[];
  widgets: ({ widgetId: string } & StageCounts)[];
  /** Distinct homes that used the tool in the period, and how many asked for a callback. */
  homes: { total: number; callbacks: number };
}

/** A home that used the quote tool. Address only: never who lives there. */
export interface HomeRecord {
  uprn: string;
  line: string | null;
  widgetId: string;
  firstSeen: string;
  lastSeen: string;
  details: { propertyType: string; bedrooms: number; heating: string } | null;
  quote: { low: number; high: number; heatPumpKw: number; grant: number; price?: number; tooBig?: boolean; noEstimate?: boolean; heatLossW?: number } | null;
  quotedAt: string | null; // when the estimate was (last) shown
  callbackRequested: boolean;
  callbackAt: string | null;
  // How the callback reached the installer: emailed, or sent as an instant quote
  callbackDelivery?: { method: "email"; to: string[]; sentAt: string } | { method: "quote"; reference: string; sentAt: string } | null;
}

/** A contact form enquiry: what about, which form, the address if the form asked. Never who sent it. */
/** A solar callback's system, priced from the installer's prices (no roof data is kept). */
export interface SolarLeadSystem {
  panels: number;
  kwp: number;
  panelModel: string;
  inverter: string;
  battery: string | null; // the battery's label, if one was chosen and offered
  low: number;
  high: number;
  usageKwh: number;
}

/** A contact form enquiry, or a solar callback (kind "solar"). */
export interface EnquiryRecord {
  id: string;
  kind: "contact" | "solar";
  widgetId: string;
  topic: string | null;
  uprn: string | null;
  line: string | null;
  createdAt: string;
  delivery: { method: "email"; to: string[]; sentAt: string } | null;
  system?: SolarLeadSystem | null; // solar only: null when the roof couldn't be scanned
}

/** One home with its EPC summary (fetched when a Leads row is opened). */
export interface HomeDetail extends HomeRecord {
  // How the callback request reached the installer (their addresses, never the homeowner's)
  callbackDelivery: { method: "email"; to: string[]; sentAt: string } | { method: "quote"; reference: string; sentAt: string } | null;
  // null = not checked (older record); summary null = no certificate on the register
  epc: { summary: { details?: Record<string, unknown>; epcRating?: string; floorArea?: number } | null } | null;
}

/** Error carrying the API's message and HTTP status. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, token: string | null, body?: unknown): Promise<T> {
  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    method,
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, json.error ?? json.message ?? `Request failed (${res.status})`);
  return json as T;
}

/** The link in a confirmation email: no sign-in, the token is the proof. */
export function confirmRecipient(companyId: string, token: string): Promise<{ email: string; confirmed: boolean }> {
  return request("POST", "/v1/recipients/confirm", null, { c: companyId, t: token });
}

// ─── Account → Users (100-api functions/team) ──────────────────────────────

export type TeamRole = "owner" | "admin" | "member";
export interface TeamMember {
  sub: string;
  email: string;
  name: string;
  role: TeamRole;
  joinedAt: string | null;
  pictureUrl: string;
}
export interface TeamInvite {
  email: string;
  role: "admin" | "member";
  invitedAt: string | null;
  invitedBy: string;
  expiresAt: string | null;
  expired: boolean;
}
export interface Team {
  members: TeamMember[];
  invites: TeamInvite[];
  me: { sub: string; role: TeamRole };
  maxUsers: number;
}

/** The invite link's details (no sign-in: the token is the proof). */
export function getInvite(token: string): Promise<{ invite: { email: string; company: string; invitedBy: string; role: TeamRole } }> {
  return request("GET", `/v1/team/invite?t=${encodeURIComponent(token)}`, null);
}

/** Accept an invite: creates the login in the company with this password. */
export function acceptInvite(token: string, firstName: string, lastName: string, password: string): Promise<{ ok: boolean; email: string }> {
  return request("POST", "/v1/team/invite/accept", null, { t: token, firstName, lastName, password });
}

/** Unauthenticated GET (e.g. /health). */
export function apiGet<T>(path: string): Promise<T> {
  return request<T>("GET", path, null);
}

/** Typed calls for signed-in pages. */
export function useApi() {
  const { idToken } = useAuth();
  return useMemo(
    () => ({
      listWidgets: () => request<{ widgets: Widget[] }>("GET", "/v1/widgets", idToken).then((r) => r.widgets),
      /** Create a component; product (default "ashp") is fixed from then on. */
      createWidget: (name: string, product: WidgetProduct = "ashp") =>
        request<{ widget: Widget }>("POST", "/v1/widgets", idToken, { name, product }).then((r) => r.widget),
      getWidget: (id: string) => request<{ widget: Widget }>("GET", `/v1/widgets/${id}`, idToken).then((r) => r.widget),
      updateWidget: (
        id: string,
        patch: {
          name?: string;
          branding?: WidgetBranding;
          pricing?: WidgetPricing;
          solarPricing?: SolarPricing | null; // solar widgets only (the API rejects it for others)
          domains?: string[];
          notifyEmails?: string[];
          hosted?: HostedDetails;
          quotes?: { enabled: boolean };
        },
      ) =>
        request<{ widget: Widget }>("PUT", `/v1/widgets/${id}`, idToken, patch).then((r) => r.widget),
      deleteWidget: (id: string) => request<unknown>("DELETE", `/v1/widgets/${id}`, idToken),
      /**
       * Solar estimate preview data for the console (signed in; nothing proprietary
       * ships in public JS): locate an address, get a ready-to-draw roof, or work
       * out the result table. Typed loosely; callers cast to the widget types.
       */
      solarPreview: <T = unknown>(widgetId: string, body: SolarPreviewRequest) =>
        request<T>("POST", `/v1/widgets/${encodeURIComponent(widgetId)}/solar-preview`, idToken, body),
      /** Is this hosted page link free? (format, reserved words, taken) */
      checkHostedLink: (code: string) =>
        request<{ code: string; available: boolean; reason?: string }>("GET", `/v1/hosted-links/${encodeURIComponent(code)}`, idToken),
      /** Choose the hosted page link once; the tool comes back with it locked. */
      setHostedLink: (widgetId: string, code: string) =>
        request<{ widget: Widget }>("POST", `/v1/widgets/${encodeURIComponent(widgetId)}/hosted-link`, idToken, { code }).then((r) => r.widget),
      getBilling: () => request<BillingOverview>("GET", "/v1/billing", idToken),
      // Account → Users
      getTeam: () => request<Team>("GET", "/v1/team", idToken),
      inviteUser: (email: string, role: "admin" | "member") => request<Team>("POST", "/v1/team/invites", idToken, { email, role }),
      resendInvite: (email: string) => request<Team>("POST", "/v1/team/invites/resend", idToken, { email }),
      revokeInvite: (email: string) => request<Team>("POST", "/v1/team/invites/revoke", idToken, { email }),
      setUserRole: (sub: string, role: "admin" | "member") => request<Team>("PUT", `/v1/team/members/${encodeURIComponent(sub)}`, idToken, { role }),
      removeUser: (sub: string) => request<Team>("DELETE", `/v1/team/members/${encodeURIComponent(sub)}`, idToken),
      /**
       * Stripe Checkout for the basket: LeadFlow ASHP or QuoteFlow ("" = neither) and/or
       * LeadFlow Solar (at least one product), plus the add-ons. Returns the URL to send the browser to.
       */
      startCheckout: (interval: "monthly" | "yearly", plan: BillingPlan | "", solar: boolean, hosting: number, solarHosting: number, contactForms: boolean) =>
        request<{ url: string }>("POST", "/v1/billing/checkout", idToken, { interval, plan, solar, hosting, solarHosting, contactForms }).then((r) => r.url),
      /**
       * Change the products and add-ons now (Stripe charges or credits the difference).
       * Plan "" with solar false ends the subscription at the period's close.
       */
      updatePlan: (plan: BillingPlan | "", solar: boolean, hosting: number, solarHosting: number, contactForms: boolean) =>
        request<BillingOverview>("POST", "/v1/billing/update", idToken, { plan, solar, hosting, solarHosting, contactForms }),
      /** Stripe customer portal (cards, invoices, VAT details, cancel). */
      openBillingPortal: () => request<{ url: string }>("POST", "/v1/billing/portal", idToken, {}).then((r) => r.url),
      getProfile: () => request<{ profile: CompanyProfile }>("GET", "/v1/profile", idToken).then((r) => r.profile),
      updateProfile: (patch: { user?: { firstName: string; lastName: string }; company?: CompanyDetails }) =>
        request<{ profile: CompanyProfile }>("PUT", "/v1/profile", idToken, patch).then((r) => r.profile),
      listRecipients: () => request<{ recipients: Recipient[] }>("GET", "/v1/recipients", idToken).then((r) => r.recipients),
      /** Add an address and email it a confirmation (or resend); your own address confirms at once. */
      addRecipient: (email: string) => request<{ recipient: Recipient }>("POST", "/v1/recipients", idToken, { email }).then((r) => r.recipient),
      getTerms: (widgetId: string) => request<TermsStatus>("GET", `/v1/widgets/${widgetId}/terms`, idToken),
      // product: "ashp" | "solar" keeps only that product's components (the Dashboard / Analytics switch)
      getStats: (range: StatsRange, widgetId?: string, product?: string) =>
        request<Stats>("GET", `/v1/stats?range=${range}${widgetId ? `&widgetId=${encodeURIComponent(widgetId)}` : ""}${product ? `&product=${product}` : ""}`, idToken),
      getAddresses: (range: StatsRange, widgetId?: string, product?: string) =>
        request<{ addresses: HomeRecord[]; total: number; callbacks: number; enquiries?: EnquiryRecord[] }>(
          "GET",
          `/v1/addresses?range=${range}${widgetId ? `&widgetId=${encodeURIComponent(widgetId)}` : ""}${product ? `&product=${product}` : ""}`,
          idToken,
        ),
      getAddress: (widgetId: string, uprn: string) =>
        request<{ address: HomeDetail }>("GET", `/v1/addresses/${encodeURIComponent(widgetId)}/${encodeURIComponent(uprn)}`, idToken).then(
          (r) => r.address,
        ),
      /** Upload a logo (PNG/JPEG/WebP, ≤ 1 MB) straight to S3; returns its public URL. */
      uploadLogo: async (widgetId: string, file: File) => {
        const { upload, publicUrl } = await request<{ upload: { url: string; fields: Record<string, string> }; publicUrl: string }>(
          "POST",
          `/v1/widgets/${encodeURIComponent(widgetId)}/logo`,
          idToken,
          { contentType: file.type, size: file.size },
        );
        const form = new FormData();
        for (const [k, v] of Object.entries(upload.fields)) form.append(k, v);
        form.append("file", file); // must be last in an S3 POST
        const res = await fetch(upload.url, { method: "POST", body: form });
        if (!res.ok) throw new ApiError(res.status, "The upload didn't work. Please try a PNG, JPEG or WebP under 1 MB.");
        return publicUrl;
      },
      acceptTerms: (widgetId: string, version: string) => request<TermsStatus>("POST", `/v1/widgets/${widgetId}/terms/accept`, idToken, { version }),
    }),
    [idToken],
  );
}
